import { useState } from "react";
import { Sparkles } from "lucide-react";
import { useApi } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useBi } from "../lib/customer";
import { useSite } from "../lib/site";
import { useFavorites, useRecent } from "../lib/store";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Sel } from "./Fields";
import PropertyCard from "./PropertyCard";

function Reason({ p }) {
  const { tr } = useI18n();
  return <p className="mt-2 text-xs text-cta/90" data-testid={`rec-reason-${p.slug}`}>{p.match != null && <span className="me-1 font-semibold">{p.match}%</span>}{tr(p, "reason")}</p>;
}

function Popular({ items }) {
  const b = useBi();
  return (
    <section data-testid="home-popular" className="container-lux py-24">
      <p className="eyebrow mb-4">{b("Trending now", "الأكثر اهتماماً")}</p>
      <h2 className="h-display mb-10 text-3xl sm:text-4xl lg:text-5xl">{b("Popular properties", "عقارات رائجة")}</h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{items.map((p) => (<PropertyCard key={p.id} p={p} />))}</div>
    </section>
  );
}

export function Recommended({ excludeId, testid = "recommended-for-you", fallback }) {
  const b = useBi();
  const recent = useRecent();
  const { favs } = useFavorites();
  const { data } = useApi(`/recommendations?ids=${recent.join(",")}&fav=${favs.join(",")}&exclude=${excludeId || ""}&limit=4`);
  if (!data?.personalized || !data.items.length) return fallback?.length ? <Popular items={fallback} /> : null;
  return (
    <section data-testid={testid} className="container-lux py-14">
      <p className="eyebrow mb-2 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" />{b("Personalized", "مخصص لك")}</p>
      <h2 className="h-display mb-8 text-3xl">{b("Recommended for you", "مقترحات لك")}</h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{data.items.map((p) => (<div key={p.id}><PropertyCard p={p} /><Reason p={p} /></div>))}</div>
    </section>
  );
}

export function PreferenceQuiz() {
  const b = useBi();
  const { lang, tr } = useI18n();
  const { locations, byKind } = useSite();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ budget: "", city: "", type: "", purpose: "" });
  const [query, setQuery] = useState(null);
  const res = useApi(query);
  const budgets = [500000, 1000000, 2000000, 3000000, 5000000, 10000000, 20000000];
  const run = () => setQuery(`/recommendations?limit=8&${new URLSearchParams(Object.entries(f).filter(([, v]) => v))}`);
  return (
    <>
      <button type="button" className="chip" onClick={() => setOpen(true)} data-testid="preference-quiz-open"><Sparkles className="h-3.5 w-3.5" />{b("Find my match", "اعثر على ما يناسبني")}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-cta/20 bg-surface text-fg" data-testid="preference-quiz">
          <DialogTitle className="h-display text-3xl">{b("Find your match", "اعثر على العقار المناسب")}</DialogTitle>
          <DialogDescription className="text-fg/60">{b("Answer four quick questions — we rank our listings by how well they fit.", "أجب عن أربعة أسئلة — وسنرتب العقارات حسب ملاءمتها لك.")}</DialogDescription>
          <div className="grid gap-3 sm:grid-cols-4">
            <Sel testid="quiz-budget" value={f.budget} onChange={(v) => setF({ ...f, budget: v })} placeholder={b("Max budget", "أقصى ميزانية")} options={budgets.map((x) => [String(x), new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(x)])} />
            <Sel testid="quiz-city" value={f.city} onChange={(v) => setF({ ...f, city: v })} placeholder={b("Any city", "أي مدينة")} options={locations.map((l) => [l.slug, tr(l, "name")])} />
            <Sel testid="quiz-type" value={f.type} onChange={(v) => setF({ ...f, type: v })} placeholder={b("Any type", "أي نوع")} options={byKind("property_type").map((c) => [c.key, tr(c, "label")])} />
            <Sel testid="quiz-purpose" value={f.purpose} onChange={(v) => setF({ ...f, purpose: v })} placeholder={b("Any purpose", "أي غرض")} options={[["sale", b("Buy", "شراء")], ["rent", b("Rent", "إيجار")], ["investment", b("Invest", "استثمار")]]} />
          </div>
          <button type="button" className="btn-cta w-fit" onClick={run} data-testid="quiz-submit">{b("Show my matches", "اعرض النتائج")}</button>
          {res.data && (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="quiz-results">
              {res.data.items.map((p) => (<div key={p.id} onClick={() => setOpen(false)} role="presentation"><PropertyCard p={p} /><Reason p={p} /></div>))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
